import React, { useState, useRef } from 'react';
import { Popover, Overlay } from 'react-bootstrap';
import { Icon, Button } from '@pwc/pwc-deals-ui';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import {
  getEnablePowerBiIngestion,
  getEnableTableauProject,
  getWorkspaceId,
} from 'features/project/projectSlice';
import featureFlags from 'common/helpers/featureFlags';

import './ObjectInjestPopover.scss';
import { getUserSettingsSelector } from '../../../../../common/redux/userSettings/userSettingsSlice';

const ObjectInjestPopoverContent = ({
  target,
  show,
  onCancel = () => {},
  onInsertText = () => {},
  onInsertPowerBi = () => {},
  onInsertTableau = () => {},
  onInsertExcelOrPPT = () => {},
  allowTextOnly,
}) => {
  const ref = useRef();
  const { url } = useRouteMatch();
  const workspaceId = useSelector(getWorkspaceId);
  const userSettings = useSelector(getUserSettingsSelector);
  const enableTableauProject = useSelector(getEnableTableauProject);
  const enablePowerBiIngestion = useSelector(getEnablePowerBiIngestion);
  const lightModeVariant = userSettings && !userSettings.settings.darkMode;
  const { t } = useTranslation();

  const handleAddLibraryItem = async (e) => {
    e.preventDefault();
    onCancel();
    if (onInsertExcelOrPPT) {
      onInsertExcelOrPPT();
    }
  };

  const handlePowerBi = (e) => {
    e.preventDefault();
    onCancel();
    if (onInsertPowerBi) {
      onInsertPowerBi();
    }
  };

  const handleTableau = (e) => {
    e.preventDefault();
    onCancel();
    if (onInsertTableau) {
      onInsertTableau();
    }
  };

  const handleText = (e) => {
    e.preventDefault();
    onCancel();
    if (onInsertText) {
      onInsertText();
    }
  };

  const textOnlyIconClassName = `outline ${
    lightModeVariant ? 'icon-grey' : 'icon-grey-medium'
  }`;

  return (
    <div ref={ref} data-testid="injest-select-popover">
      <Overlay
        show={show}
        target={target}
        rootClose
        onHide={onCancel}
        container={ref.current}
        placement="bottom"
      >
        <Popover className="injest-select-popover">
          <Popover.Content className="popup-menu popup-confirmation injest-select-popover">
            {allowTextOnly ? (
              // eslint-disable-next-line react/button-has-type
              <button
                className="ui-button button-menu button-no-underline allowTextOnly"
                disabled
              >
                <Icon
                  name="table-data"
                  width={24}
                  height={24}
                  className={textOnlyIconClassName}
                />
                <span>Excel or PowerPoint</span>
              </button>
            ) : (
              <Link
                className="ui-button button-menu button-no-underline"
                to={`${url}/library-items`}
                onClick={handleAddLibraryItem}
              >
                <Icon
                  name="table-data"
                  width={24}
                  height={24}
                  className="outline icon-orange-theme"
                />
                <span>{t('excelOrPowerPoint')}</span>
              </Link>
            )}

            {featureFlags.isPowerBiIngestionEnabled && allowTextOnly && (
              // eslint-disable-next-line react/button-has-type
              <button
                className="ui-button button-menu button-no-underline allowTextOnly"
                disabled
              >
                <Icon
                  name="powerbi-new"
                  width={24}
                  height={24}
                  className={textOnlyIconClassName}
                />
                <span>Power BI</span>
              </button>
            )}

            {featureFlags.isPowerBiIngestionEnabled && !allowTextOnly && (
              <Button
                className="button-no-underline button-menu"
                onClick={handlePowerBi}
                disabled={!enablePowerBiIngestion}
              >
                <Icon
                  name="powerbi-new"
                  width={24}
                  height={24}
                  className={
                    enablePowerBiIngestion
                      ? 'outline icon-orange-theme'
                      : 'outline icon-grey-theme-disabled'
                  }
                />
                <span>{t('powerBI')}</span>
              </Button>
            )}

            {featureFlags.isTableauEnabled &&
              enableTableauProject &&
              workspaceId && (
                <Button
                  className={`button-no-underline button-menu ${
                    allowTextOnly ? 'allowTextOnly' : ''
                  }`}
                  disabled={!!allowTextOnly}
                  onClick={handleTableau}
                >
                  <Box sx={{ transform: 'scale(1.4)' }}>
                    <Icon
                      name="grow-revenue-2"
                      width={24}
                      height={24}
                      className={
                        allowTextOnly
                          ? textOnlyIconClassName
                          : 'outline icon-orange-theme'
                      }
                    />
                  </Box>
                  <span>{t('tableau')}</span>
                </Button>
              )}
            <Button
              className="button-no-underline button-menu"
              onClick={handleText}
            >
              <Icon
                name="textbox"
                width={24}
                height={24}
                className="outline icon-orange-theme"
              />
              <span>{t('textFormat')}</span>
            </Button>
          </Popover.Content>
        </Popover>
      </Overlay>
    </div>
  );
};

const ObjectInjestPopover = ({
  onInsertText = () => {},
  onPopover = () => {},
  onInsertExcelOrPPT = () => {},
  onInsertPowerBi = () => {},
  onInsertTableau = () => {},
  droppableLocation,
  allowTextOnly,
}) => {
  const target = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const showPopover = (e) => {
    e.preventDefault();
    setIsOpen(true);
    onPopover(true);
  };

  const hidePopover = () => {
    setIsOpen(false);
    onPopover(false);
  };

  return (
    <>
      <div ref={target}>
        <Button
          className="ui-button button-link object-injest-toggle"
          onClick={showPopover}
        >
          <Icon name="plus" width={24} className="outline icon-orange" />
        </Button>
      </div>
      <ObjectInjestPopoverContent
        target={target}
        show={isOpen}
        onCancel={hidePopover}
        onInsertText={onInsertText}
        onInsertPowerBi={onInsertPowerBi}
        onInsertTableau={onInsertTableau}
        onInsertExcelOrPPT={onInsertExcelOrPPT}
        droppableLocation={droppableLocation}
        allowTextOnly={allowTextOnly}
      />
      <p className="object-injest-label">{t('addAnItem')}</p>
    </>
  );
};

export default ObjectInjestPopover;
